import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { ParseError, parsePhoneNumberWithError } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { CountryCodeEnum } from '@/types/regionTypes';
import useMemoizedFn from '@/utils/useMemoizedFn';

import { isStoreHit } from '@/components/AlgoliaSearchInput/utils';
import { Box } from '@/components/Box';
import { CheckboxField } from '@/components/CheckboxField';
import { Col } from '@/components/Col';
import { TitleField } from '@/components/FormFields/TitleField';
import { FormProvider } from '@/components/FormProvider';
import { Icon } from '@/components/Icon';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { ReCaptcha } from '@/components/ReCaptcha';
import { SelectField } from '@/components/SelectField';
import {
  getLangSlugForAlgolia,
  getTranslatedStoreValue,
} from '@/components/StoresList/utils';
import { TextAreaField } from '@/components/TextAreaField';
import { Typography } from '@/components/Typography';
import { useRouter } from '@/hooks/useRouter';
import { getIndexName } from '@/integrations/algolia/helpers';
import { retailerWidgetAlgoliaClient } from '@/integrations/algolia/retailerWidgetClient';
import type { StoreHit } from '@/integrations/algolia/types';
import type { DateInfo, Schedule } from '@/lib/boutique/helpers';
import { formatTimesOption } from '@/lib/boutique/helpers';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import {
  formatNationalByPhonePrefix,
  getExamplePhone,
  isValidPhoneNumber,
  parsePhoneNumber,
} from '@/lib/phone';
import { getPhonePrefixSelectOpts } from '@/lib/phonePrefixes/helpers';
import { getRegionFromLocale } from '@/lib/regions';
import {
  commonMessages as appointmentMessages,
  ConfirmLegal,
  KeepInformed,
} from '@/messages/appointment';
import { commonMessages } from '@/messages/common';
import { formMessages, PersonalDataPolicy } from '@/messages/forms';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { AppointmentDataType } from '@/views/AppointmentView/helpers';
import {
  fetchAppointment,
  handleCalendarMonthChangeUtil,
  useCalendarMonthTranslation,
} from '@/views/AppointmentView/helpers';
import { getNameFieldOrder, SortedNameFields } from '@/views/SignUpView/helper';

import { AppointmentConfirmation } from '../AppointmentConfirmation/AppointmentConfirmation';
import { isAppointmentFormField } from './helpers';
import { appointmentSchema } from './schema';

import { ReactComponent as GroupSVG } from '@/assets/icons/group-icon.svg';

import * as S from './AppointmentForm.styles';
const today = new Date();

const tomorrow = new Date(today);
tomorrow.setDate(today.getDate() + 1);

interface AppointmentFormProps {
  multiDoors?: string[] | null;
  retailerId?: string;
  sku?: string;
}

export const AppointmentForm = ({
  multiDoors,
  retailerId,
  sku,
}: AppointmentFormProps) => {
  const { formatMessage } = useIntl();
  const {
    country,
    locale: regionProviderLocale,
    query: { languageCode },
  } = useRegionProvider();
  const { locale } = useRouter();
  const {
    locale: { sfCode },
  } = getRegionFromLocale(locale);
  const index = useMemo(
    () => retailerWidgetAlgoliaClient.initIndex(getIndexName('STORE', locale)),
    [locale],
  );
  const [utc_offset, setUtc_offset] = useState('');
  const [holidayHours, setHolidayHours] = useState<DateInfo[] | undefined>();
  const [reopenDate, setReopenDate] = useState<string | undefined>();
  const [nonWorkingDays, setNonWorkingDays] = useState<string[] | undefined>();
  const [doorLocations, setDoorLocations] = useState<
    { label: string; value: string }[]
  >([]);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [date, setDate] = useState(dayjs(tomorrow).format('YYYY-MM-DD'));
  const [appointments, setAppointments] = useState<Schedule>();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    firstName: string;
    lastName: string;
    title: {
      label?: string;
      value?: string;
    } | null;
  } | null>(null);
  const [availableTimes, setAvailableTimes] = useState([
    { label: '11:00 - 12:00', value: '11:00 - 12:00' },
    { label: '12:00 - 13:00', value: '12:00 - 13:00' },
    { label: '13:00 - 14:00', value: '13:00 - 14:00' },
    { label: '14:00 - 15:00', value: '14:00 - 15:00' },
    { label: '15:00 - 16:00', value: '15:00 - 16:00' },
  ]);
  const [examplePhone, setExamplePhone] = useState<string>('');
  const [activeStartMonth, setActiveStartMonth] = useState(
    tomorrow.getMonth() + 1,
  );

  const reCaptchaRef = useRef<ReCAPTCHA>(null);

  useCalendarMonthTranslation(activeStartMonth, regionProviderLocale.sfCode);

  const isJapan = country.code === CountryCodeEnum.JP;
  const schema = appointmentSchema(
    formatMessage,
    country,
    isJapan,
    doorLocations,
  );
  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });
  const captcha = formCtx.watch('captcha');

  const [isVerifyingRecaptcha, setIsVerifyingRecaptchaStatus] = useState(false);

  useEffect(() => {
    const dataPhone = formCtx.watch('phone');
    const dataPhonePrefix = formCtx.watch('phonePrefix');
    const fullPhoneNumber = dataPhonePrefix?.value + dataPhone;
    const isValidPhone = isValidPhoneNumber(fullPhoneNumber);
    if (isValidPhone) {
      const phoneNumber = parsePhoneNumber(fullPhoneNumber);
      formCtx.setValue(
        'phone',
        formatNationalByPhonePrefix(
          dataPhonePrefix.value,
          phoneNumber?.formatNational(),
        ),
      );
    }
    const phoneNumber = getExamplePhone(dataPhonePrefix.code as CountryCode);
    if (phoneNumber) setExamplePhone(phoneNumber);
  }, [formCtx.watch('phone'), formCtx.watch('phonePrefix')]);

  const initBoutiqueData = useMemoizedFn(async () => {
    if (retailerId) {
      try {
        const store = await index.getObjects([retailerId]).then((res) => {
          const result = res.results[0];
          if (!isStoreHit(result)) {
            throw new Error('Invalid store data');
          }
          return result;
        });
        setHolidayHours(store?.temporary_hours?.holiday_hours ?? undefined);
        setReopenDate(store?.temporary_hours?.reopen_date ?? undefined);
        setAppointments(store?.appointments as Schedule);
        setUtc_offset(store?.utc_offset as string);
        formatTimesOption(
          store?.appointments as Schedule,
          tomorrow,
          store?.temporary_hours?.holiday_hours ?? undefined,
          setAvailableTimes,
          setNonWorkingDays,
          reopenDate,
        );
      } catch (e) {
        if (e instanceof Error) setError(e.message);
      }
    }
  });

  useEffect(() => {
    initBoutiqueData();
  }, [retailerId, country.code]);

  useEffect(() => {
    if (appointments) {
      if (retailerId) {
        formatTimesOption(
          appointments,
          date,
          holidayHours ?? undefined,
          setAvailableTimes,
          setNonWorkingDays,
          reopenDate,
        );
      }
    }
  }, [date]);

  useEffect(() => {
    if (appointments && holidayHours) {
      formatTimesOption(
        appointments,
        date,
        holidayHours,
        setAvailableTimes,
        setNonWorkingDays,
        reopenDate,
      );
    }
  }, [appointments, holidayHours, date]);

  useEffect(() => {
    if (availableTimes) formCtx.setValue('timeSlot', availableTimes[0]);
  }, [availableTimes]);

  useEffect(() => {
    if (multiDoors && multiDoors.length > 0) {
      index
        .getObjects(multiDoors, {
          attributesToRetrieve: [
            'city',
            `city_${getLangSlugForAlgolia(regionProviderLocale.slug)}`,
            'street',
            `street_${getLangSlugForAlgolia(regionProviderLocale.slug)}`,
            'offers_appointment',
          ],
        })
        .then(({ results }) => {
          const locations = results
            .filter(
              (result): result is StoreHit =>
                result !== null &&
                (result as StoreHit).offers_appointment === true,
            )
            .map((result) => {
              const city = getTranslatedStoreValue(
                result,
                'city',
                regionProviderLocale.slug,
              );
              const street = getTranslatedStoreValue(
                result,
                'street',
                regionProviderLocale.slug,
              );

              return {
                label: `${city} - ${street}`,
                value: `${result.objectID}`,
              };
            });
          setDoorLocations(locations);
        });
    }
  }, [multiDoors, index]);

  const phonePrefix = formCtx.watch('phonePrefix');
  const phone = formCtx.watch('phone');
  useEffect(() => {
    if (phone && phonePrefix?.value) {
      try {
        const phoneNumber = parsePhoneNumberWithError(
          phone,
          phonePrefix.code as CountryCode,
        );
        formCtx.setValue(
          'phone',
          formatNationalByPhonePrefix(
            phonePrefix.value,
            phoneNumber.formatNational(),
          ),
        );

        formCtx.clearErrors('phone');
      } catch (error) {
        if (error instanceof ParseError) {
          formCtx.setError('phone', {
            message: formatMessage(formMessages.invalidPhoneNumber),
          });
        } else {
          formCtx.setError('phone', {
            message: formatMessage(formMessages.technicalError),
          });
        }
      }
    }
  }, [phone, phonePrefix, formCtx, formatMessage]);

  if (!retailerId) return null;

  const handleMonthChange = handleCalendarMonthChangeUtil(setActiveStartMonth);

  const futureDate = new Date(tomorrow);
  futureDate.setDate(tomorrow.getDate() + 29);

  const handleSubmit = async (data: any) => {
    setProcessing(true);
    data = { ...data, date: date };
    const time = date + data.timeSlot.value;
    const isoTime = dayjs(time).format('YYYY-MM-DDTHH:mm:ss');
    const startDateTime = isoTime + utc_offset;
    const AppointmentData: AppointmentDataType = {
      salutation: data.title?.value ?? '',
      phone: data.phonePrefix.value + data.phone.replace(/\D/g, ''),
      reason: data.reason.value,
      websiteCountryCode: country.code,
      languageCode: sfCode,
      locationId: data.locationId?.value || retailerId,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      comments: data.comments,
      startDateTime: startDateTime,
      newsletterOptIn: data.newsletterOptIn,
      ...(!!data.firstNameKanji && { firstNameKanji: data.firstNameKanji }),
      ...(!!data.lastNameKanji && { lastNameKanji: data.lastNameKanji }),
      reserveProductSku: sku,
      source: 'Widget',
    };

    try {
      const isValidPhone = isValidPhoneNumber(AppointmentData.phone ?? '');
      if (!isValidPhone) {
        setProcessing(false);
        formCtx.setError('phone', {
          type: 'manual',
          message: formatMessage(
            appointmentMessages.btqAppointmentPhoneNumberError,
          ),
        });
        return;
      }

      const appointmentRes = await fetchAppointment(AppointmentData);
      if (appointmentRes.success) {
        setProcessing(false);
        setIsSubmitted(true);
        setConfirmationData({
          firstName: data.firstName,
          lastName: data.lastName,
          title: data.title,
        });
      } else {
        setProcessing(false);

        for (const field of appointmentRes.invalid) {
          if (!isAppointmentFormField(field) || !field) {
            setError(formatMessage({ id: 'genericError' }));
            continue;
          }

          const fieldSchema = schema.fields[field];
          if ((fieldSchema as any)?.type === 'ref') continue;

          setError(formatMessage({ id: 'genericError' }));
          formCtx.setError(field, {
            type: 'manual',
            message: formatMessage(formMessages.invalidField),
          });
        }
        setError(formatMessage({ id: 'genericError' }));
      }
    } catch (e) {
      setProcessing(false);
      setError(formatMessage({ id: 'genericError' }));
    }
  };

  const onChange = (newDate: any) => {
    setDate(dayjs(newDate).format('YYYY-MM-DD'));
  };

  if (isSubmitted && confirmationData) {
    return (
      <AppointmentConfirmation
        countryCode={country.code}
        firstName={confirmationData.firstName}
        lastName={confirmationData.lastName}
        title={confirmationData.title}
      />
    );
  }

  return (
    <>
      <Box bg='lightGrey' pt={5}>
        <S.AppointmentHeader
          colorized={true}
          data-testid='sectionheader'
          sizeDesktop={50}
          sizeMobile={40}
          text={formatMessage(commonMessages.bookAppointment)}
          textAlign='center'
        />
      </Box>
      <S.GroupIconWrapper>
        <GroupSVG />
      </S.GroupIconWrapper>
      <Box bg='lightGrey'>
        <S.BookOpetionRow>
          <Col>
            <Typography color='default' mb={4} textAlign='center'>
              {formatMessage(appointmentMessages.personalAdvice)}
            </Typography>
          </Col>
        </S.BookOpetionRow>
      </Box>
      <Box>
        <Box bg='lightGrey'>
          <FormProvider
            name='appointment'
            {...formCtx}
            onSubmit={(data) => {
              handleSubmit(data);
            }}
          >
            <Box bg='white' pl={2} pr={2}>
              <S.Row gapDensity='none'>
                <Col>
                  <Typography
                    color='secondary'
                    fontWeight='bold'
                    mt={4}
                    textAlign='center'
                  >
                    {formatMessage(appointmentMessages.appointmentDate)}
                  </Typography>
                </Col>
              </S.Row>
              <S.CalendarWrapper>
                <S.CalendarStyled
                  formatShortWeekday={(_locale, value) =>
                    ['S', 'M', 'T', 'W', 'T', 'F', 'S'][value.getDay()]
                  }
                  locale='EN'
                  maxDate={futureDate}
                  minDate={tomorrow}
                  nextLabel={
                    <Icon
                      color='primary'
                      name='chevronRight'
                      size='l'
                      variant='regular'
                    />
                  }
                  onActiveStartDateChange={handleMonthChange}
                  onChange={onChange}
                  prevLabel={
                    <Icon
                      color='primary'
                      name='chevronLeft'
                      size='l'
                      variant='regular'
                    />
                  }
                  showNeighboringMonth={false}
                  tileDisabled={({ date }) => {
                    const formattedDate = dayjs(date).format('YYYY-MM-DD');
                    return nonWorkingDays?.includes(formattedDate) ?? false;
                  }}
                  value={date}
                />
              </S.CalendarWrapper>
              <S.Row>
                <Col>
                  <Typography color='default' pb={4} pt={2} textAlign='left'>
                    {formatMessage(appointmentMessages.contactBoutiqueText)}
                  </Typography>
                </Col>
              </S.Row>
            </Box>
            <Box pl={2} pr={2}>
              <S.Row gapDensity='none'>
                {doorLocations.length > 0 && (
                  <>
                    <Col>
                      <Typography
                        color='secondary'
                        fontWeight='bold'
                        mt={4}
                        textAlign='center'
                      >
                        {formatMessage(appointmentMessages.storeText)}
                      </Typography>
                    </Col>
                    <Col>
                      <SelectField
                        name='locationId'
                        options={doorLocations}
                        placeholder={formatMessage(commonMessages.select)}
                        required
                      />
                    </Col>
                  </>
                )}
                <Col>
                  <Typography
                    color='secondary'
                    fontWeight='bold'
                    mt={4}
                    textAlign='center'
                  >
                    {formatMessage(appointmentMessages.timeSlotText)}
                  </Typography>
                </Col>
                <Col>
                  <SelectField
                    name='timeSlot'
                    options={availableTimes}
                    placeholder={formatMessage(commonMessages.select)}
                    required
                  />
                </Col>
                <Col>
                  <Typography mt={2} textAlign='left'>
                    {formatMessage(appointmentMessages.validAppointmentText)}
                  </Typography>
                </Col>
              </S.Row>
              <S.Row gapDensity='none'>
                <Col>
                  <Typography
                    color='secondary'
                    fontWeight='bold'
                    mt={4}
                    scale='h5'
                    textAlign='center'
                  >
                    {formatMessage(appointmentMessages.personalInfo)}
                  </Typography>
                </Col>
                <Col>
                  <TitleField countryCode={country.code} />
                </Col>
              </S.Row>
              <S.InputRow gapDensity='none'>
                <SortedNameFields
                  arr={[
                    {
                      component: (
                        <Col key='firstName'>
                          <InputField
                            className={CONTENTSQUARE_MASKED}
                            data-testid='firstname'
                            name='firstName'
                            placeholder={formatMessage(
                              appointmentMessages.firstNamePlaceHolder,
                            )}
                            required
                          />
                        </Col>
                      ),
                      order: getNameFieldOrder(
                        country,
                        regionProviderLocale,
                        'first',
                      ),
                    },
                    {
                      component: (
                        <Col key='lastName'>
                          <InputField
                            className={CONTENTSQUARE_MASKED}
                            data-testid='lastname'
                            name='lastName'
                            placeholder={formatMessage(
                              appointmentMessages.lastNamePlaceHolder,
                            )}
                            required
                          />
                        </Col>
                      ),
                      order: getNameFieldOrder(
                        country,
                        regionProviderLocale,
                        'last',
                      ),
                    },
                    ...(isJapan
                      ? [
                          {
                            component: (
                              <Col key='firstNameKanji'>
                                <InputField
                                  className={CONTENTSQUARE_MASKED}
                                  data-testid='firstNameKanji'
                                  name='firstNameKanji'
                                  placeholder={formatMessage(
                                    formMessages.firstNameKanji,
                                  )}
                                  required
                                />
                              </Col>
                            ),
                            order: getNameFieldOrder(
                              country,
                              regionProviderLocale,
                              'firstKanji',
                            ),
                          },
                          {
                            component: (
                              <Col key='lastNameKanji'>
                                <InputField
                                  className={CONTENTSQUARE_MASKED}
                                  data-testid='lastNameKanji'
                                  name='lastNameKanji'
                                  placeholder={formatMessage(
                                    formMessages.lastNameKanji,
                                  )}
                                  required
                                />
                              </Col>
                            ),
                            order: getNameFieldOrder(
                              country,
                              regionProviderLocale,
                              'lastKanji',
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
                <Col>
                  <InputField
                    className={CONTENTSQUARE_MASKED}
                    data-testid='email'
                    name='email'
                    placeholder={formatMessage(
                      appointmentMessages.emailPlaceHolder,
                    )}
                    required
                  />
                </Col>
                <Col>
                  <SelectField
                    name='phonePrefix'
                    options={getPhonePrefixSelectOpts()}
                  />
                </Col>
                <Col>
                  <InputField
                    autoComplete='phone'
                    name='phone'
                    pattern='[0-9]+'
                    placeholder={formatMessage(
                      appointmentMessages.phonePlaceHolder,
                    )}
                    required
                    type='text'
                  />
                  <Typography
                    color='secondaryGrey'
                    scale='p3'
                    textAlign='right'
                  >
                    {examplePhone}
                  </Typography>
                </Col>
              </S.InputRow>
              <S.Row gapDensity='none'>
                <Col>
                  <Typography
                    color='secondary'
                    fontWeight='bold'
                    mb={2}
                    mt={4}
                    scale='h5'
                    textAlign='center'
                  >
                    {formatMessage(appointmentMessages.bookAppointmentTo)}
                  </Typography>
                </Col>
                <Col>
                  <SelectField
                    defaultValue={{
                      label: formatMessage(
                        appointmentMessages.discover_and_try,
                      ),
                      value: 'discover_and_try',
                    }}
                    name='reason'
                    options={[
                      {
                        label: formatMessage(
                          appointmentMessages.discover_and_try,
                        ),
                        value: 'discover_and_try',
                      },
                      {
                        label: formatMessage(
                          appointmentMessages.find_a_new_strap,
                        ),
                        value: 'find_a_new_strap',
                      },
                      {
                        label: formatMessage(
                          appointmentMessages.take_care_of_my_breitling,
                        ),
                        value: 'take_care_of_my_breitling',
                      },
                      {
                        label: formatMessage(appointmentMessages.purchase),
                        value: 'purchase',
                      },
                      {
                        label: formatMessage(appointmentMessages.other),
                        value: 'other',
                      },
                    ]}
                    placeholder={formatMessage(
                      appointmentMessages.reasonPlaceHolder,
                    )}
                  />
                </Col>
              </S.Row>
              <S.TextRow>
                <S.TextAreaCol>
                  <TextAreaField
                    name='comments'
                    placeholder={formatMessage(
                      appointmentMessages.tellUsSomething,
                    )}
                    rows={5}
                  />
                </S.TextAreaCol>
                <S.CheckBoxCol>
                  <CheckboxField name='newsletterOptIn'>
                    <S.LegalTextWrapper>
                      <S.OptinOptionsText>{KeepInformed}</S.OptinOptionsText>
                      <br />
                      <S.OptinOptionsText>
                        {PersonalDataPolicy}
                      </S.OptinOptionsText>
                      <br />
                    </S.LegalTextWrapper>
                  </CheckboxField>
                  <CheckboxField name='consent'>
                    <S.LegalTextWrapper>
                      <S.OptinOptionsText>{ConfirmLegal}</S.OptinOptionsText>
                    </S.LegalTextWrapper>
                  </CheckboxField>
                </S.CheckBoxCol>
              </S.TextRow>
              <S.Row gapDensity='none'>
                <S.RecaptChaCol>
                  <ReCaptcha
                    ref={reCaptchaRef}
                    languageCode={languageCode}
                    name='captcha'
                    onChange={setIsVerifyingRecaptchaStatus}
                    validation={false}
                  />
                </S.RecaptChaCol>
                {error && (
                  <Col md={12}>
                    <Typography color='error' textAlign='center'>
                      {error}
                    </Typography>
                  </Col>
                )}
                <S.AppointmentButtonCol>
                  <LegacyButton
                    data-testid='buttonSign'
                    disabled={!captcha || isVerifyingRecaptcha}
                    isFluid
                    isProcessing={processing || isVerifyingRecaptcha}
                    type='submit'
                  >
                    {formatMessage(commonMessages.bookAppointment)}
                  </LegacyButton>
                </S.AppointmentButtonCol>
              </S.Row>
            </Box>
          </FormProvider>
        </Box>
      </Box>
    </>
  );
};
